import Modal from "react-modal"
import { FormProvider, useForm } from "react-hook-form"
import { useState, useRef } from "react"
import isEmail from "validator/lib/isEmail"
import isMobilePhone from "validator/lib/isMobilePhone"
import { ErrorMessage } from "@/components/checkout-form/errorMessage"
import getInputValidationClasses from "@/lib/helpers/getInputValidationClasses"
import ReCAPTCHA from "react-google-recaptcha"
import { input } from "@/common/form/input.module.css"
import { updateMemberContact } from "@/lib/api/member"
import { getMemberToken } from "@/lib/helpers/member"
import Button from "@/components/common/button/button"
import Close from "@/components/common/icons/close"
import styles from "./contact-modal.module.css"
import classNames from "classnames"

export default function ContactModal({ contact, isOpen, setIsOpen, onSave }) {
  const [inProgress, setProgress] = useState(false)
  const [message, setMessage] = useState(null)
  const recaptchaRef = useRef()
  const siteKey = process.env.NEXT_PUBLIC_CAPTCHA_SITE_KEY
  const methods = useForm({
    mode: "all",
    defaultValues: {
      email: contact?.email ?? "",
      phone: contact?.phone ?? "",
      mobilePhone: contact?.mobilePhone ?? ""
    }
  })

  const { control, register, handleSubmit } = methods

  const closeModal = () => {
    setIsOpen(false)
  }

  Modal.setAppElement("html")

  const customStyles = {
    overlay: {
      display: "flex",
      backgroundColor: "rgba(0, 0, 0, .5)",
      zIndex: 1000,
      alignItems: "center",
      justifyContent: "center"
    }
  }

  const updateDetails = async (values) => {
    if (inProgress) {
      return false
    }

    setProgress(true)
    setMessage(null)

    const token = getMemberToken()
    const captcha = await recaptchaRef.current.executeAsync()
    const response = await updateMemberContact(token, captcha, values)

    setMessage(response.status)
    setProgress(false)
    setIsOpen(false)

    if (typeof onSave === "function") {
      onSave(values, response)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      style={customStyles}
      shouldCloseOnEsc={true}
      shouldReturnFocusAfterClose={true}
      shouldCloseOnOverlayClick={true}
      onRequestClose={() => closeModal()}
      className={classNames(styles.modal)}>
      <ReCAPTCHA
        className="hidden"
        ref={recaptchaRef}
        size="invisible"
        sitekey={siteKey}
      />
      <div className="click" onClick={() => closeModal()}>
        <div className="close">
          <Close className="icon" />
        </div>
      </div>
      <div className="content-wrapper">
        <h2>Ändra kontaktuppgifter</h2>
        <FormProvider {...methods}>
          <form className={styles.form} onSubmit={handleSubmit(updateDetails)}>
            <label name="phone">Telefon</label>
            <input
              className={classNames(
                input,
                getInputValidationClasses("phone", control)
              )}
              id="phone"
              {...register("phone")}
            />
            <ErrorMessage name="phone" />
            <label name="mobilePhone">Mobiltelefon</label>
            <input
              className={[
                input,
                getInputValidationClasses("mobilePhone", control)
              ].join(" ")}
              id="mobilePhone"
              {...register("mobilePhone", {
                validate: (value) =>
                  !value ||
                  isMobilePhone(value, "sv-SE") ||
                  "Ange ett giltigt mobiltelefonnummer."
              })}
            />
            <ErrorMessage name="mobilePhone" />
            <label name="email">E-post</label>
            <input
              className={[
                input,
                getInputValidationClasses("email", control)
              ].join(" ")}
              id="email"
              {...register("email", {
                required: true,
                validate: (value) =>
                  isEmail(value) || "Ange en giltig e-postadress."
              })}
            />
            <ErrorMessage name="email" />
            <div className="button-wrapper">
              <Button
                theme="lime"
                ariaLabel="Spara"
                className="button"
                loading={inProgress}
                type="submit">
                Spara
              </Button>
              <Button
                theme="secondary"
                ariaLabel="Avbryt"
                className="button"
                onClick={() => closeModal()}>
                Avbryt
              </Button>
            </div>
            {message && (
              <div className="message">
                <p>{message}</p>
              </div>
            )}
          </form>
        </FormProvider>
      </div>
    </Modal>
  )
}
